import React, { useState, } from 'react';
import { Dialog, DialogActions, DialogContent, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, SelectProps, TextField, Checkbox, Radio, RadioGroup, FormControlLabel, Box, Chip, CircularProgress } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CancelIcon from "@mui/icons-material/Cancel";
import { FieldAttributes, Form, Formik, useField, useFormikContext } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { StyledDropzone } from '../Elements/Dropzone';
import { UsersModel } from '../../models/UsersModel';
import { PartnerModel, StatesModel } from '../../Pages/RequestSignature';
import * as yup from 'yup';
import { FMTextField } from '../Elements/input';
import { GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../../helper/theme';


export interface AddRequestSignatureModel {
    partnersId: number;
    signerUserId: number;
    stateId: number;
    nameOfForm: string;
    dueDate: string;
    isWetSignature: boolean;
    isElectronicSignature: boolean;
    isDigitalSignature: boolean;
    notarizationRequired: boolean;
    files: File[];
};
export const InitNewRequestSignatureModel: AddRequestSignatureModel = {
    partnersId: 0,
    signerUserId: 0,
    stateId: 0,
    nameOfForm: '',
    dueDate: '',
    isWetSignature: false,
    isElectronicSignature: false,
    isDigitalSignature: false,
    notarizationRequired: undefined as unknown as boolean,
    files: [],
};

interface Iprops{
    Open: boolean;
    handleClose: (isReload: boolean) => void;
    partners: PartnerModel[];
    states: StatesModel[];
    users: UsersModel[];
    loadingUsers?: boolean;
    onClientChange?: (partnersId: number) => void;
};

const AddRequestSignatureValidationSchema = yup.object({
    partnersId: yup.number().min(1, 'Please select a Client').required('Client is required'),
    signerUserId: yup.number().min(1, 'Please select a Signer after selecting a Client').required('Signer is required'),
    stateId: yup.number().min(1, 'Please select a State').required('State is required'),
    nameOfForm: yup.string().trim().required('Name of Form is required'),
    dueDate: yup.string().required('Due Date is required'),
    isWetSignature: yup.boolean(),
    isElectronicSignature: yup.boolean(),
    isDigitalSignature: yup.boolean(),
    notarizationRequired: yup.boolean().oneOf([true, false], 'Please select Yes or No').required('Notarization Option is required'),
});

export const AddRequestSignature: React.FC<Iprops> = ({ Open, handleClose, partners, states, users, loadingUsers = false, onClientChange }) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);

    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);
        const { setFieldValue } = useFormikContext<AddRequestSignatureModel>();
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>Select Client</FormHelperText>
                <Select {...field}
                    labelId="Partner-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    onChange={(e) => {
                        const partnersId = e.target.value as number;
                        helpers.setValue(partnersId);
                        setFieldValue('signerUserId', 0); // Reset signer when partner changes
                        // Call the callback to fetch users for the selected client
                        if (onClientChange && partnersId > 0) {
                            onClientChange(partnersId);
                        }
                    }}
                >
                    {partners.map((partner, key) =>
                        <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                    )}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectSignerUser: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        const { values } = useFormikContext<AddRequestSignatureModel>();
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>Select Signer</FormHelperText>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Select {...field}
                        labelId="Signer-Select-Label"
                        className="border-round custom-menu-item"
                        IconComponent={loadingUsers ? () => null : ExpandMoreIcon}
                        label={null}
                        disabled={values.partnersId === 0 || loadingUsers}
                        placeholder={values.partnersId === 0 ? 'Select a Client before selecting a Signer' : 'Select a Signer'}
                        sx={{
                            width: '100%',
                            '& .MuiSelect-icon': {
                                display: loadingUsers ? 'none' : 'block',
                            },
                        }}
                    >
                        {users.map((user, key) => (
                            <MenuItem key={key} value={user.userId}>{user.firstName} {user.lastName}</MenuItem>
                        ))}
                    </Select>
                    {loadingUsers && (
                        <CircularProgress
                            size={20}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                right: '14px',
                                marginTop: '-10px',
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                </Box>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectStates: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>Select State</FormHelperText>
                <Select {...field}
                    labelId="State-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                >
                    {states.map((state, key) => (
                        <MenuItem key={key} value={state.stateId}>{state.stateName}</MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectNotarization: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
                    <FormHelperText className='formtexthelper-heading' sx={{ flexShrink: 0, margin: 0 }}>Notarization Required:</FormHelperText>
                    <Select name={field.name}
                        value={field.value === true ? 'true' : field.value === false ? 'false' : ''}
                        displayEmpty
                        renderValue={(v) => {
                            if (v === 'true') return 'Yes';
                            if (v === 'false') return 'No';
                            return 'Select Yes or No';
                        }}
                        onChange={(e) => {
                            const val = e.target.value as string;
                            if (val === '') {
                                helpers.setValue(undefined as unknown as boolean);
                            } else {
                                helpers.setValue(val === 'true');
                            }
                        }}
                        onBlur={field.onBlur}
                        labelId="Notarization-Select-Label"
                        className="border-round custom-menu-item"
                        IconComponent={ExpandMoreIcon}
                        label={null}
                        sx={{ textAlign: 'left', minWidth: 120 }}
                    >
                        <MenuItem value="" disabled>Select Yes or No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                    </Select>
                </Box>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };

    return (
        <Dialog open={Open} onClose={handleClose} maxWidth='xl' sx={{ width: '80%', margin: '0 auto' }}>
            {loading && <LinearProgress style={{ margin: 2 }} />}
            <h2 style={{ marginLeft: "1vw" }} className='card-title'>
                Create New Request for Signature
            </h2>
            <Formik initialValues={InitNewRequestSignatureModel}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnMount={true}
                validationSchema={AddRequestSignatureValidationSchema}
                validate={(values) => {
                    const errors: any = {};
                    // Check if at least one signature type is selected
                    if (!values.isWetSignature && !values.isElectronicSignature && !values.isDigitalSignature) {
                        errors.signatureType = 'At least one signature type (Wet, Electronic, or Digital) must be selected';
                    }
                    return errors;
                }}
                onSubmit={(data, { setSubmitting, setErrors }) => {
                    console.log('data', data);
                    const clientName = partners.find((partner) => partner.partnersId === data.partnersId)?.customerName || 'the selected partner';
                    Swal.fire({
                        title: '<div style="color: white;">Are you sure?</div>',
                        html: `<div style="color: white;">This will send a signature request to the Client Portal for <span style="color: yellow;">${clientName}</span></div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, Proceed',
                        cancelButtonText: 'No, Continue to EDIT',
                        confirmButtonColor: GreenBtnColor,
                        cancelButtonColor: OrangeBtnColor,
                        reverseButtons: true,
                        background: PrimaryBlueColor,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSubmitting(true);
                            setLoading(true);
                            
                            // Prepare FormData for API call with files and form data
                            const formData = new FormData();
                            
                            // Append JSON data as a string field (will be in request.body)
                            formData.append("data", JSON.stringify({  // everything except files
                                partnersId: data.partnersId,
                                signerUserId: data.signerUserId,
                                
                                stateId: data.stateId,
                                nameOfForm: data.nameOfForm,
                                dueDate: data.dueDate,
                                
                                isWetSignature: data.isWetSignature,
                                isElectronicSignature: data.isElectronicSignature,
                                isDigitalSignature: data.isDigitalSignature,
                                notarizationRequired: data.notarizationRequired,
                            }));
                            
                            // Append files - all files with the same field name for multer
                            // Multer will put them in request.files as an array
                            if (data.files && data.files.length > 0) {
                                data.files.forEach((file) => {
                                    formData.append("files", file);
                                });
                            }
                            
                            apiClient('/SignatureRequests/addSignatureRequest', {
                                method: 'POST',
                                headers: new Headers({
                                    'Authorization': `Bearer ${token}`,
                                }),
                                body: formData,
                            }).then((response) => {
                                console.log('response', response);
                                if(!response) return;
                                if(response.status) {
                                    Swal.fire({
                                        title: 'Success',
                                        text: response.message || 'The signature request has been sent to the client.',
                                        icon: 'success',
                                    });
                                    handleClose(true);
                                } else {
                                    throw new Error(response.message || 'Failed to send signature request to client.');
                                }
                            }).catch((error) => {
                                console.error('error', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: error.message || 'Failed to send signature request to client.',
                                    icon: 'error',
                                });
                            }).finally(() => {
                                setSubmitting(false);
                                setLoading(false);
                            });
                        }else{
                            setSubmitting(false);
                            setLoading(false);
                        }
                    });
                }}
            >
                {({ values, isSubmitting, setFieldValue, setFieldTouched, errors, touched, isValid, validateForm }) => {
                    // Get today's date in YYYY-MM-DD format for the date input min attribute
                    const today = new Date();
                    const todayString = today.toISOString().split('T')[0];
                    
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { name, type } = e.target;
                        const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
                        setFieldValue(name, value);
                        setFieldTouched(name, true);
                        // If it's a signature type checkbox, validate the form to check the group validation
                        if (name === 'isWetSignature' || name === 'isElectronicSignature' || name === 'isDigitalSignature') {
                            setTimeout(() => validateForm(), 0);
                        }
                    };
                    // Check for signature type validation error - only show if user has interacted with signature checkboxes
                    const signatureTypeTouched = touched.isWetSignature || touched.isElectronicSignature || touched.isDigitalSignature;
                    const signatureTypeError = signatureTypeTouched ? (errors as any).signatureType : undefined;
                    return (
                    <Form className='main-form'>
                        <DialogContent>
                            <Grid container spacing={2}>
                                {/* Step 1: Select Client and Signer */}
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FMSelectPartnersCustom name="partnersId" />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FMSelectSignerUser name="signerUserId" />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <FMSelectStates name="stateId" />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <FormHelperText className='formtexthelper-heading'>Type Name of Form</FormHelperText>
                                                <FMTextField name="nameOfForm" value={values.nameOfForm} 
                                                    onChange={handleChange} 
                                                    label={null} 
                                                    type="text" 
                                                    fullWidth variant="outlined" 
                                                    sx={{ m: 2 }} 
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <FormHelperText className='formtexthelper-heading'>Select Due Date</FormHelperText>
                                                <FMTextField name="dueDate" value={values.dueDate} 
                                                    onChange={handleChange} 
                                                    label={null} 
                                                    type="date" 
                                                    fullWidth variant="outlined" 
                                                    sx={{ m: 2 }} 
                                                    inputProps={{ min: todayString }}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth error={!!signatureTypeError}>
                                        <FormHelperText className='formtexthelper-heading'>Signature Type:</FormHelperText>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                            <FormControlLabel label="Wet Signature" 
                                                control={
                                                    <Checkbox name="isWetSignature"
                                                        checked={values.isWetSignature || false}
                                                        onChange={handleChange}
                                                    />
                                                } 
                                            />
                                            <FormControlLabel label="Electronic Signature" 
                                                control={
                                                    <Checkbox name="isElectronicSignature"
                                                        checked={values.isElectronicSignature || false}
                                                        onChange={handleChange}
                                                    />
                                                } 
                                            />
                                            <FormControlLabel label="Digital Signature" 
                                                control={
                                                    <Checkbox name="isDigitalSignature"
                                                        checked={values.isDigitalSignature || false}
                                                        onChange={handleChange}
                                                    />
                                                } 
                                            />
                                        </Box>
                                        {signatureTypeError && (
                                            <FormHelperText error>{signatureTypeError}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FMSelectNotarization name="notarizationRequired" />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <FormControl sx={{ width: '100%' }} 
                                        // error={touched.file && !!errors.file}
                                    >
                                        <FormHelperText className='formtexthelper-heading'>
                                            Select File(s) or Drag and Drop
                                        </FormHelperText>
                                        <StyledDropzone label="Select File(s) or Drag and Drop" 
                                            onFileDrop={(files: File[]) => {
                                                setFieldValue("files", [ ...values.files, ...files ]);
                                                // setFieldTouched("files", true);
                                            }} 
                                        />
                                        {/* Display selected files */}
                                        {values.files && values.files.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <FormHelperText sx={{ mb: 1 }}>
                                                    Selected Files ({values.files.length}):
                                                </FormHelperText>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {values.files.map((file, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={`${file.name}`}
                                                            onDelete={() => {
                                                                const updatedFiles = values.files.filter((_, i) => i !== index);
                                                                setFieldValue("files", updatedFiles);
                                                            }}
                                                            deleteIcon={<CancelIcon />}
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ 
                                                                maxWidth: '100%',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {/* {touched.file && errors.file && (
                                            <FormHelperText error>{errors.file}</FormHelperText>
                                        )} */}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <button type='button' onClick={() => {handleClose(false);}} 
                                className="btn-2 btn d-flex btn-primary search-btn " 
                            >Cancel</button>
                            <button type="submit" disabled={isSubmitting || !isValid} 
                                className="btn btn-primary active-btn-blue search-btn "
                            >Send to Client</button>
                        </DialogActions>
                    </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};